import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ExpandableTextProps {
  text: string;
  /**
   * Ties the toggle to the text it opens, through `aria-controls`. Required
   * because two of these can share a page and a duplicated id would point both
   * controls at the same paragraph.
   */
  id: string;
  /** How many lines survive the clamp. */
  lines?: 2 | 3;
  /** The caller's width and type scale — everything else is fixed here. */
  className?: string;
}

/** Spelled out so Tailwind's build-time scanner can find both. */
const CLAMP: Record<2 | 3, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

/**
 * Prose that is clamped by default and can be opened in place.
 *
 * Extracted from the board, which grew it first and for a specific reason: a
 * long workflow description took eight lines and a third of the viewport before
 * a single card was visible. The graph page then wanted the same treatment, and
 * a second copy of the measuring logic would have drifted from this one the
 * first time either was touched.
 *
 * **The measurement is the whole difficulty.** Whether text is clipped is not
 * knowable from the text — it depends on how wide the column is — so it has to
 * be measured after layout and re-measured whenever the column resizes. That is
 * what the `ResizeObserver` is for, and it is why the toggle can only be
 * rendered after a measurement rather than guessed at from a character count.
 *
 * Deliberately not a `title` tooltip, which is what both call sites used to do:
 * there is no keyboard route to a tooltip and nothing at all on touch, so the
 * full text was reachable by mouse alone.
 *
 * Width and type scale belong to the caller. The board sets `text-sm` over its
 * columns; the graph page sets `text-xs` inside a dense strip above its canvas.
 * Neither is this component's business — what it owns is the clamp, the
 * measurement and the control.
 */
export function ExpandableText({
  text,
  id,
  lines = 2,
  className,
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = ref.current;
    // Measured only while clamped. Expanded, the clamp is off and the two
    // heights agree by definition — recomputing there would decide the text no
    // longer overflows and take the "Show less" control away mid-read.
    if (!element || expanded) return;

    const measure = () => {
      // The one-pixel margin is for sub-pixel line heights, which otherwise
      // report a fraction of a pixel of overflow on text that fits exactly.
      setClipped(element.scrollHeight > element.clientHeight + 1);
    };
    measure();

    // Whether two lines is enough depends on how wide those lines are, so this
    // is a question that has to be re-asked when the column changes size.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [text, expanded]);

  return (
    <div className={cn('flex flex-col items-start gap-0.5', className)}>
      <p
        ref={ref}
        id={id}
        className={cn('text-muted-foreground', !expanded && CLAMP[lines])}
      >
        {text}
      </p>
      {/*
        Rendered only when there is actually more to see. A "Show more" above a
        description that already fits is a control that does nothing, and the
        reader has no way to know that before pressing it.
      */}
      {clipped ? (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={() => {
            setExpanded((open) => !open);
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      ) : null}
    </div>
  );
}
